/**
 * Shared message ordering for the four conversation panes (customer, shipper,
 * airline, transport). All four had their own copy of the same comparator, and
 * all four had the same bug.
 *
 * The bug: parseEmailMessage coerced an unparseable Gmail internalDate to 0,
 * while the comparator guarded with `Number.isFinite(internalDate)` — and 0 is
 * finite. So a message with no usable timestamp sorted as epoch 1970 and jumped
 * to the TOP of the thread, above a reply it was actually answering. The same 0
 * failed EmailThread's separate `ms > 0` check, so that row alone fell back to
 * the raw locale string and rendered in a different date format — the visible
 * tell that two code paths disagreed about what "no timestamp" means.
 */

/**
 * The message's send time in epoch ms, or null when we genuinely don't know.
 *
 * Deliberately refuses to re-parse the human-readable `date` string. It is a
 * locale-formatted value ("7/26/2026, 12:34:47 AM" vs "26/07/2026"), so parsing
 * it silently swaps day and month in en-GB. An unknown time we can handle; a
 * confidently wrong one reorders the thread and looks authoritative.
 */
export function messageTimeMs(msg) {
  if (!msg) return null;
  const raw = msg.internalDate;
  if (raw === null || raw === undefined || raw === "") return null;
  const ms = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return ms;
}

/**
 * De-duplicate by id and order oldest-first.
 *
 * Messages with no usable timestamp sort LAST and keep their arrival order
 * among themselves. In practice such a message is one that just arrived over
 * SSE, so "newest" is the right guess — and it is certainly a better one than
 * 1970, which pinned it above the entire thread.
 */
export function sortThreadMessages(messages) {
  if (!Array.isArray(messages)) return [];

  const seen = new Set();
  const unique = messages.filter((m) => {
    const key = m?.id || m?.messageId;
    if (!key) return true; // keep un-keyed entries rather than collapsing them
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Decorate with the original index so Array.prototype.sort stability is not
  // relied upon across engines for the unknown-time group.
  return unique
    .map((msg, index) => ({ msg, index, time: messageTimeMs(msg) }))
    .sort((a, b) => {
      if (a.time === null && b.time === null) return a.index - b.index;
      if (a.time === null) return 1; // unknown -> last
      if (b.time === null) return -1;
      if (a.time !== b.time) return a.time - b.time;
      return a.index - b.index;
    })
    .map((entry) => entry.msg);
}
